import { useEffect, useState, type ReactNode } from "react";
import { format, parseISO } from "date-fns";
import { Check } from "lucide-react";

import { Button, Drawer } from "@/design-system/primitives";
import { Icon } from "@/lib/Icon";
import { track } from "@/lib/analytics";
import { useCancelGuidedSelfInjection } from "@/lib/api/hooks";
import type { Appointment, GuidedSelfInjectionResource } from "@/types/api";

interface GuidedInjectionDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  appointment: Appointment | null | undefined;
  resource: GuidedSelfInjectionResource;
  clinicianName: string;
  onReschedule: () => void;
}

type DetailsTab = "prepare" | "edit";

const JOIN_WINDOW_MS = 15 * 60 * 1000;

export function GuidedInjectionDetailsDrawer({
  open,
  onClose,
  appointment,
  resource,
  clinicianName,
  onReschedule,
}: GuidedInjectionDetailsDrawerProps) {
  const [tab, setTab] = useState<DetailsTab>("prepare");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const cancel = useCancelGuidedSelfInjection();

  useEffect(() => {
    if (!open) return;
    setTab("prepare");
    setConfirmCancel(false);
  }, [open]);

  useEffect(() => {
    if (!open || appointment?.status !== "scheduled") return;
    setNowMs(Date.now());
    const interval = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(interval);
  }, [appointment?.start_at, appointment?.status, open]);

  const joinWindowOpen = Boolean(
    appointment &&
    appointment.status === "scheduled" &&
    nowMs >= parseISO(appointment.start_at).getTime() - JOIN_WINDOW_MS,
  );
  const canJoin = joinWindowOpen && Boolean(appointment?.video_url);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Guided self-injection"
      ariaLabel="Guided self-injection details"
    >
      <AppointmentDetails
        appointment={appointment}
        clinicianName={clinicianName}
        canJoin={canJoin}
        joinWindowOpen={joinWindowOpen}
      />

      <div className="grid grid-cols-2 gap-2 border-t border-line-subtle bg-white p-2">
        <TabButton active={tab === "prepare"} onClick={() => setTab("prepare")}>
          Prepare
        </TabButton>
        <TabButton active={tab === "edit"} onClick={() => setTab("edit")}>
          Edit appointment
        </TabButton>
      </div>

      {tab === "prepare" ? (
        <PreparationContent resource={resource} />
      ) : (
        <div className="px-15 py-20">
          {appointment ? (
            <>
              {appointment.status !== "scheduled" ? (
                <p className="border border-line-subtle bg-sunken p-12 font-body text-13 text-[#7C7C7C]">
                  This guided session is complete.
                </p>
              ) : !confirmCancel ? (
                <>
                  <p className="font-body text-13 text-[#7C7C7C]">
                    Change the appointment time or cancel this session.
                  </p>
                  <div className="mt-15 grid grid-cols-2 gap-8">
                    <Button variant="secondary" onClick={onReschedule}>
                      Reschedule
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setConfirmCancel(true)}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <div className="border border-line-subtle bg-sunken p-12">
                  <p className="font-body text-13 text-ink">
                    Cancel this appointment? You can book another time
                    afterward.
                  </p>
                  <div className="mt-10 grid grid-cols-2 gap-8">
                    <Button
                      variant="secondary"
                      disabled={cancel.isPending}
                      onClick={() => setConfirmCancel(false)}
                    >
                      Keep it
                    </Button>
                    <Button
                      variant="danger"
                      loading={cancel.isPending}
                      onClick={() =>
                        cancel
                          .mutateAsync(appointment.id)
                          .then(() => {
                            track("guided_injection_appointment_cancelled", {
                              appointment_id: appointment.id,
                            });
                            onClose();
                          })
                          .catch(() => undefined)
                      }
                    >
                      Cancel appointment
                    </Button>
                  </div>
                  {cancel.error && (
                    <p role="alert" className="mt-8 type-body-sm text-danger">
                      {(cancel.error as { message?: string }).message ??
                        "The appointment could not be cancelled."}
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="font-body text-14 text-[#7C7C7C]">
              There is no active appointment to manage.
            </p>
          )}
        </div>
      )}
    </Drawer>
  );
}

function AppointmentDetails({
  appointment,
  clinicianName,
  canJoin,
  joinWindowOpen,
}: {
  appointment: Appointment | null | undefined;
  clinicianName: string;
  canJoin: boolean;
  joinWindowOpen: boolean;
}) {
  if (!appointment) {
    return (
      <div className="px-15 py-20">
        <p className="font-body text-14 text-[#7C7C7C]">
          There is no active appointment.
        </p>
      </div>
    );
  }

  const start = parseISO(appointment.start_at);
  const end = appointment.end_at ? parseISO(appointment.end_at) : null;
  const scheduled = appointment.status === "scheduled";

  return (
    <section className="px-15 py-20" aria-labelledby="guided-visit-details">
      <div className="flex items-center gap-6">
        <span aria-hidden className="inline-flex h-12 w-12">
          <Icon name="Checkmark" />
        </span>
        <p className="font-mono text-11 uppercase text-[#929292]">
          {scheduled ? "Scheduled" : "Visit complete"}
        </p>
      </div>
      <h2
        id="guided-visit-details"
        className="mt-8 font-mono text-18 uppercase leading-110"
      >
        {format(start, "EEEE, MMMM d")}
      </h2>
      <p className="mt-4 font-body text-15 text-ink">
        {format(start, "h:mm a")}
        {end ? `–${format(end, "h:mm a")}` : ""}
      </p>
      <p className="mt-5 font-body text-13 text-[#7C7C7C]">
        Guided self-injection with {clinicianName}
      </p>

      {scheduled &&
        (canJoin ? (
          <a
            href={appointment.video_url!}
            target="_blank"
            rel="noreferrer"
            className="mt-15 flex h-44 w-full items-center justify-between bg-black px-15 font-body text-14 text-white"
            onClick={() =>
              track("guided_injection_join_clicked", {
                appointment_id: appointment.id,
              })
            }
          >
            <span>Join video</span>
            <span className="inline-flex w-8 items-center justify-center">
              <Icon name="Arrow" viewBox="0 0 10 12" />
            </span>
          </a>
        ) : (
          <button
            type="button"
            disabled
            className="mt-15 flex min-h-44 w-full cursor-not-allowed items-center justify-between gap-10 bg-[#D9D9D9] px-15 py-8 text-left text-[#777]"
          >
            <span className="font-body text-14">Join video</span>
            <span className="text-right font-mono text-10 uppercase leading-110">
              {joinWindowOpen
                ? "Link available soon"
                : "Available 15 minutes before"}
            </span>
          </button>
        ))}
    </section>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`h-40 font-body text-[1.3rem] transition-colors ${
        active
          ? "bg-[var(--accent)] text-white"
          : "bg-[var(--accent-hover)] text-white/70 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function PreparationContent({
  resource,
}: {
  resource: GuidedSelfInjectionResource;
}) {
  return (
    <div className="px-15 py-20">
      <p className="font-mono text-11 uppercase text-[#929292]">
        Preparation guide
      </p>
      <h2 className="mt-8 font-mono text-18 uppercase leading-110">
        {resource.title}
      </h2>
      <p className="mt-8 font-body text-14 leading-140 text-[#7C7C7C]">
        {resource.intro ||
          "Review what to have ready before your guided injection visit."}
      </p>

      {resource.checklist.length > 0 && (
        <section className="mt-25">
          <h3 className="font-mono text-12 uppercase">Have these ready</h3>
          <ul className="mt-10 grid gap-8">
            {resource.checklist.map((item) => (
              <li
                key={item}
                className="flex gap-8 border border-line-subtle bg-raised p-12 font-body text-13"
              >
                <Check
                  size={15}
                  strokeWidth={1.5}
                  className="mt-1 shrink-0 text-success"
                  aria-hidden
                />
                {item}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-25 flex flex-col gap-25">
        {resource.sections.map((section) => (
          <section key={section.title}>
            <h3 className="font-mono text-12 uppercase">{section.title}</h3>
            <p className="mt-8 whitespace-pre-line font-body text-14 leading-150 text-[#7C7C7C]">
              {section.body}
            </p>
          </section>
        ))}
      </div>

      <aside className="mt-30 border border-line-subtle bg-sunken p-15 font-body text-13 text-[#7C7C7C]">
        This visit is for live injection guidance. If you feel unwell or have an
        urgent medical concern, do not wait for this appointment—contact your
        care team or seek urgent care.
      </aside>
    </div>
  );
}
